import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import type { AssetPropertyHistoryEntry, PropertyDefinition } from '../../hooks/useQueries'
import { PropertySourceBadge } from '../PropertySourceBadge'
import { EnumPropertyDisplay, getBooleanDisplay } from './PropertyInputUtils'
import { Badge } from '../ui/badge'
import { EmptyState } from '../ui/empty-state'

interface PropertyHistoryTimelineProps {
    org: string
    entries: AssetPropertyHistoryEntry[]
    propertyDefinition?: PropertyDefinition
    showRetracted: boolean
}

// A cluster is a group of entries whose [start_date, end_date) ranges overlap.
// When a cluster has more than one entry the highest-priority one is marked as
// `active` and the others render stacked underneath.
interface EntryCluster {
    entries: AssetPropertyHistoryEntry[]
}

function rangesOverlap(a: AssetPropertyHistoryEntry, b: AssetPropertyHistoryEntry): boolean {
    const aStart = a.start_date ? new Date(a.start_date).getTime() : -Infinity
    const aEnd = a.end_date ? new Date(a.end_date).getTime() : Infinity
    const bStart = b.start_date ? new Date(b.start_date).getTime() : -Infinity
    const bEnd = b.end_date ? new Date(b.end_date).getTime() : Infinity
    return aStart < bEnd && bStart < aEnd
}

function clusterEntries(entries: AssetPropertyHistoryEntry[]): EntryCluster[] {
    const clusters: EntryCluster[] = []
    for (const entry of entries) {
        const match = clusters.find(c => c.entries.some(e => rangesOverlap(e, entry)))
        if (match) {
            match.entries.push(entry)
        } else {
            clusters.push({ entries: [entry] })
        }
    }
    return clusters
}

function highestPriorityEntry(entries: AssetPropertyHistoryEntry[]): AssetPropertyHistoryEntry {
    return entries.reduce((best, e) => {
        const bestPrio = best.priority ?? 0
        const ePrio = e.priority ?? 0
        return ePrio > bestPrio ? e : best
    }, entries[0])
}

function formatAbsoluteDate(iso: string | null | undefined, locale: string): string | null {
    if (!iso) return null
    try {
        return new Date(iso).toLocaleDateString(locale, { year: 'numeric', month: 'short', day: 'numeric' })
    } catch {
        return iso
    }
}

function HistoryEntryCard({ org, entry, active, retracted }: { org: string; entry: AssetPropertyHistoryEntry; active: boolean; retracted: boolean }) {
    const { t, i18n } = useTranslation('assets')
    const fromLabel = formatAbsoluteDate(entry.start_date, i18n.language) ?? t('detail.history_indefinite')
    const toLabel = entry.end_date ? formatAbsoluteDate(entry.end_date, i18n.language) : t('detail.history_now')
    const validity = t('detail.history_validity', { from: fromLabel, to: toLabel })

    const rawValue = entry.value
    const displayValue = renderHistoryValue(rawValue, org, entry.property_uid ?? undefined)

    return (
        <div
            className={
                'border border-border rounded-md p-3 space-y-2 ' +
                (retracted ? 'opacity-60 bg-muted/40' : 'bg-card') +
                (active ? ' ring-1 ring-primary' : '')
            }
            data-testid="property-history-entry"
        >
            <div className="flex items-start justify-between gap-2">
                <div className="text-sm font-medium text-foreground flex-1 min-w-0 break-words">
                    {displayValue}
                </div>
                {active && (
                    <Badge variant="default" className="shrink-0 text-xs">
                        {t('detail.history_active')}
                    </Badge>
                )}
            </div>

            <div className="text-xs text-muted-foreground">{validity}</div>

            <div className="flex items-center gap-2 flex-wrap">
                <PropertySourceBadge
                    org={org}
                    sourceCode={entry.source_code}
                    sourceDetails={entry.source_details as Record<string, unknown> | null | undefined}
                    createdByName={entry.created_by_name}
                    updatedByName={entry.updated_by_name}
                    approvedByName={(entry as any).approved_by_name}
                    approvedAt={(entry as any).approved_at}
                    flags={entry.flags ?? undefined}
                />
                {entry.updated_at && (
                    <span className="text-xs text-muted-foreground">
                        {formatAbsoluteDate(entry.updated_at, i18n.language)}
                    </span>
                )}
            </div>

            {entry.flags && entry.flags.length > 0 && (
                <div className="flex items-center gap-1 flex-wrap">
                    {entry.flags.map(flag => (
                        <Badge key={flag} variant="outline" className="text-xs">
                            {flag}
                        </Badge>
                    ))}
                </div>
            )}

            {retracted && entry.deleted_at && (
                <div className="text-xs text-muted-foreground italic">
                    {t('detail.history_retracted', { date: formatAbsoluteDate(entry.deleted_at, i18n.language) })}
                </div>
            )}
        </div>
    )
}

function renderHistoryValue(value: unknown, org: string, propertyUid: string | undefined) {
    if (value === null || value === undefined || value === '') return '—'
    if (typeof value === 'boolean') return getBooleanDisplay(value)
    if (typeof value === 'string' && propertyUid) {
        return <EnumPropertyDisplay propertyDefinitionUid={propertyUid} value={value} org={org} />
    }
    if (typeof value === 'object') {
        try {
            return <code className="text-xs">{JSON.stringify(value)}</code>
        } catch {
            return String(value)
        }
    }
    return String(value)
}

export function PropertyHistoryTimeline({ org, entries, showRetracted }: PropertyHistoryTimelineProps) {
    const { t } = useTranslation('assets')

    const visibleEntries = useMemo(
        () => (showRetracted ? entries : entries.filter(e => !e.deleted_at)),
        [entries, showRetracted]
    )

    // Cluster in the original (newest-first) ordering so `activeEntryUid` is
    // chosen deterministically even when priorities tie.
    const clusters = useMemo(() => clusterEntries(visibleEntries), [visibleEntries])

    if (visibleEntries.length === 0) {
        return (
            <EmptyState
                variant="inline"
                className="py-4"
                description={t('detail.property_history_empty')}
                data-testid="property-history-empty"
            />
        )
    }

    return (
        <div className="space-y-3" data-testid="property-history-timeline">
            {clusters.map((cluster, idx) => {
                const hasOverlap = cluster.entries.length > 1
                const activeEntry = hasOverlap ? highestPriorityEntry(cluster.entries) : cluster.entries[0]
                return (
                    <div key={idx} className={hasOverlap ? 'border-l-2 border-primary/30 pl-3 space-y-2' : 'space-y-2'}>
                        {cluster.entries.map(entry => (
                            <HistoryEntryCard
                                key={entry.uid}
                                org={org}
                                entry={entry}
                                active={entry.uid === activeEntry.uid}
                                retracted={Boolean(entry.deleted_at)}
                            />
                        ))}
                    </div>
                )
            })}
        </div>
    )
}
